import { useId } from 'react';
import { Link } from '@tanstack/react-router';

import type { IntelligenceInsight, IntelligencePersona } from '../api/schemas';
import { insightLink } from '../lib/intelligence-view';

/** "2:00 p. m." stays on one line: the spaces inside a time never break. */
export const keepTimesTogether = (text: string) => text.replace(/(\d) ([ap])\. m\./g, '$1 $2. m.');

/** The finding that leads the page: what is wrong, what to do, and one clear button to go do it. */
export function LeadInsight({ insight }: { insight: IntelligenceInsight }) {
  const titleId = useId();
  const link = insightLink(insight);
  return (
    <>
      <h3 className="insight-lead-title" id={titleId}>{keepTimesTogether(insight.title)}</h3>
      <p className="insight-detail">{keepTimesTogether(insight.detail)}</p>
      <InsightSteps steps={insight.steps} titleId={titleId} />
      <Link to={link.to} search={link.search} className="btn insight-lead-action">
        {link.label}
      </Link>
    </>
  );
}

/**
 * Every other finding, once: the headline and its signer read closed; the
 * steps and the button open in place, so the page stays one screen of titles.
 */
export function InsightRow({ insight, signer }: { insight: IntelligenceInsight; signer: IntelligencePersona | undefined }) {
  const titleId = useId();
  const link = insightLink(insight);
  return (
    <details className="insight-row">
      <summary className="insight-row-summary">
        <span className="insight-row-text">
          <span className="row-title" id={titleId}>{keepTimesTogether(insight.title)}</span>
          <span className="row-sub">{keepTimesTogether(insight.detail)}</span>
          {signer ? <span className="insight-byline">{`${signer.name} · ${signer.title}`}</span> : null}
        </span>
        <svg className="insight-row-chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </summary>
      <div className="insight-row-body">
        <InsightSteps steps={insight.steps} titleId={titleId} />
        <Link to={link.to} search={link.search} className="insight-action">
          {link.label}
        </Link>
      </div>
    </details>
  );
}

/** A plain finding card, for a worker that predates the asesores. */
export function InsightCard({ insight }: { insight: IntelligenceInsight }) {
  const titleId = useId();
  const link = insightLink(insight);
  return (
    <article className="card">
      <p className="row-title" id={titleId}>{keepTimesTogether(insight.title)}</p>
      <p className="insight-detail">{keepTimesTogether(insight.detail)}</p>
      <InsightSteps steps={insight.steps} titleId={titleId} />
      <Link to={link.to} search={link.search} className="insight-action">
        {link.label}
      </Link>
    </article>
  );
}

/** "Qué hacer": the server's steps as a numbered list, named after their finding for screen readers. */
function InsightSteps({ steps, titleId }: { steps: string[] | undefined; titleId: string }) {
  const headingId = useId();
  if (!steps?.length) return null;
  return (
    <div className="insight-steps">
      <h4 className="section-label" id={headingId}>Qué hacer</h4>
      <ol className="insight-steps-list" aria-labelledby={`${headingId} ${titleId}`}>
        {steps.map((step) => (
          <li className="insight-detail" key={step}>{keepTimesTogether(step)}</li>
        ))}
      </ol>
    </div>
  );
}
